// Cart Drawer JavaScript Functionality

// This file is included from several places that can each appear multiple times
// on one page (each sample card, the samples bar, and the cart drawer section).
// Guard so the top-level declarations run only once — otherwise re-executing
// `const CartDrawerUtils` throws:
//   "Identifier 'CartDrawerUtils' has already been declared".
if (!window.__cartDrawerJsLoaded) {
  window.__cartDrawerJsLoaded = true;

// Shared utility functions
const CartDrawerUtils = {
  // Check if a product is a sample by fetching product data
  async isSampleProduct(itemHandle) {
    try {
      const productResponse = await fetch(`/products/${itemHandle}.js`);
      const productData = await productResponse.json();
      return productData.tags && productData.tags.includes('cart-sample');
    } catch (error) {
      console.error('Error fetching product data for:', itemHandle, error);
      return false;
    }
  },

  // Check if a product is excluded from gifts/samples
  async isExcludedProduct(itemHandle) {
    try {
      const productResponse = await fetch(`/products/${itemHandle}.js`);
      const productData = await productResponse.json();
      return productData.tags && productData.tags.includes('gift-sample-excluded');
    } catch (error) {
      console.error('Error fetching product data for:', itemHandle, error);
      return false;
    }
  },

  // Categorize cart items into regular products, samples, excluded products, and free gifts
  async categorizeCartItems(cartItems) {
    const regularProducts = [];
    const sampleProducts = [];
    const excludedProducts = [];
    
    for (const item of cartItems) {
      const isFreeGift = item.properties && item.properties._free_gift;
      
      if (isFreeGift) {
        continue; // Skip free gifts
      }
      
      const isSample = await this.isSampleProduct(item.handle);
      if (isSample) {
        sampleProducts.push(item);
      } else {
        const isExcluded = await this.isExcludedProduct(item.handle);
        if (isExcluded) {
          excludedProducts.push(item);
        } else {
          regularProducts.push(item);
        }
      }
    }
    
    return { regularProducts, sampleProducts, excludedProducts };
  },

  // Check if cart has at least one eligible product
  async hasEligibleProducts(cartItems) {
    const { regularProducts } = await this.categorizeCartItems(cartItems);
    return regularProducts.length > 0;
  },

  // Calculate eligible cart total (excluded products NEVER count toward thresholds)
  async getEligibleCartTotal(cartItems) {
    let eligibleTotal = 0;
    let hasEligibleProducts = false;
    
    // Only count eligible products - excluded products never count toward thresholds
    for (const item of cartItems) {
      const isFreeGift = item.properties && item.properties._free_gift;
      if (isFreeGift) continue;
      
      const isSample = await this.isSampleProduct(item.handle);
      if (isSample) continue;
      
      const isExcluded = await this.isExcludedProduct(item.handle);
      if (!isExcluded) {
        eligibleTotal += item.final_line_price;
        hasEligibleProducts = true;
      }
    }
    
    return { total: eligibleTotal, hasEligible: hasEligibleProducts };
  },

  // Determine if exclusion message should be shown
  async shouldShowExclusionMessage(cartItems) {
    const { excludedProducts } = await this.categorizeCartItems(cartItems);
    if (excludedProducts.length === 0) return false;
    
    const hasEligible = await this.hasEligibleProducts(cartItems);
    return !hasEligible;
  },

  // Remove items from cart
  async removeCartItems(items) {
    for (const item of items) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: item.key,
          quantity: 0
        })
      });
    }
  }
};

// Cart Drawer Manager - hooks into existing cart-drawer element
class CartDrawerManager {
  constructor() {
    this.config = window.cartDrawerConfig || {};
    this.freeGiftManager = null;
    this.sampleCleanupManager = null;
    this.offerBannerManager = null;
    this.cartDrawerElement = null;
    this.init();
  }

  init() {
    document.addEventListener('click', (event) => {
      const btn = event.target.closest('button[name="checkout"]');
      if (!btn) return;
      const isOnCartPage = document.querySelector('.shopify-section--main-cart') !== null;
      if (isOnCartPage) return;
      btn.setAttribute('aria-busy', 'true');
    });

    // Wait for cart-drawer element to be available
    this.waitForCartDrawer().then(() => {
      this.cartDrawerElement = document.getElementById('cart-drawer') || document.querySelector('cart-drawer');
      
      // Initialize free gift manager
      if (this.config.freeGiftEnabled && this.config.freeGiftConfig) {
        this.initFreeGiftManager();
      } else {
        // When the native free gift manager is disabled it won't fire cart:refresh
        // after cart:change. Without that refresh the cart drawer only re-renders
        // from the bundled snapshot and third-party app blocks (e.g. omake) that
        // independently modify the cart are never reflected in real time.
        let refreshTimeout = null;
        document.addEventListener('cart:change', () => {
          clearTimeout(refreshTimeout);
          refreshTimeout = setTimeout(() => {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }, 300);
        });
      }

      // Initialize sample cleanup manager
      if (this.config.samplesEnabled) {
        this.initSampleCleanupManager();
      }

      // Initialize offer banner manager
      if (this.config.offerBannerConfig) {
        this.initOfferBannerManager();
      }

      // Initialize samples bar visibility manager
      if (this.config.samplesEnabled) {
        this.initSamplesBarManager();
      }

      // Initialize product card sample handler
      this.initSampleCardHandler();

      // Listen for drawer open events to refresh samples bar
      if (this.cartDrawerElement) {
        this.cartDrawerElement.addEventListener('dialog:after-show', () => {
          if (this.samplesBarUpdateHandler) {
            this.samplesBarUpdateHandler();
          }
        });
      }
    });
  }

  waitForCartDrawer() {
    return new Promise((resolve) => {
      const cartDrawer = document.getElementById('cart-drawer') || document.querySelector('cart-drawer');
      if (cartDrawer) {
        resolve();
        return;
      }

      const observer = new MutationObserver((mutations, obs) => {
        const cartDrawer = document.getElementById('cart-drawer') || document.querySelector('cart-drawer');
        if (cartDrawer) {
          obs.disconnect();
          resolve();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Also check on DOMContentLoaded
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          const cartDrawer = document.getElementById('cart-drawer') || document.querySelector('cart-drawer');
          if (cartDrawer) {
            observer.disconnect();
            resolve();
          }
        });
      }
    });
  }

  // Free Gift Manager
  initFreeGiftManager() {
    if (!window.freeGiftManager && (this.config.freeGiftConfig || this.config.freeGiftConfig2)) {
      this.freeGiftManager = new DrawerFreeGiftManager({
        gift1: this.config.freeGiftConfig,
        gift2: this.config.freeGiftConfig2
      });
      window.freeGiftManager = this.freeGiftManager;
    }
  }

  // Sample Cleanup Manager
  initSampleCleanupManager() {
    if (!window.sampleCleanupManager) {
      this.sampleCleanupManager = new DrawerSampleCleanupManager();
      window.sampleCleanupManager = this.sampleCleanupManager;
    }
  }

  // Offer Banner Manager
  initOfferBannerManager() {
    if (!window.cartOfferBannerManager && this.config.offerBannerConfig) {
      this.offerBannerManager = new CartOfferBannerManager(this.config.offerBannerConfig);
      window.cartOfferBannerManager = this.offerBannerManager;
    }
  }

  // Samples Bar Visibility Manager
  initSamplesBarManager() {
    if (window.samplesBarInitialized) return;
    window.samplesBarInitialized = true;
    
    const updateSamplesBar = async () => {
      try {
        const button = document.getElementById('samples-bar-button');
        const buttonText = button?.querySelector('.samples-button-text');
        const samplesBar = document.querySelector('.cart-samples-bar');
        
        if (!button || !buttonText || !samplesBar) return;
        
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();
        
        let hasSample = false;
        
        for (const item of cartData.items) {
          if (await CartDrawerUtils.isSampleProduct(item.handle)) {
            hasSample = true;
            break;
          }
        }
        
        // Check if we have eligible products (hide samples bar if only excluded products)
        const hasEligible = await CartDrawerUtils.hasEligibleProducts(cartData.items);
        
        if (hasSample || !hasEligible) {
          samplesBar.classList.add('display-hidden');
        } else {
          samplesBar.classList.remove('display-hidden');
        }
        
        const config = window.samplesBarConfig;
        if (config?.linkText) {
          buttonText.textContent = config.linkText;
        }
        
        button.dataset.mode = 'select';
        delete button.dataset.sampleKey;
      } catch (error) {
        console.error('Error checking cart for samples:', error);
      }
    };
    
    let isUpdating = false;
    let updateTimeout = null;
    
    const debouncedUpdateSamplesBar = async () => {
      if (isUpdating) return;
      isUpdating = true;
      
      await updateSamplesBar();
      
      isUpdating = false;
      
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      updateTimeout = setTimeout(async () => {
        if (!isUpdating) {
          await updateSamplesBar();
        }
      }, 500);
    };

    this.samplesBarUpdateHandler = debouncedUpdateSamplesBar;
    
    document.addEventListener('DOMContentLoaded', debouncedUpdateSamplesBar);
    document.addEventListener('cart:change', debouncedUpdateSamplesBar);
    document.addEventListener('cart:refresh', debouncedUpdateSamplesBar);
    document.documentElement.addEventListener('cart:refresh', debouncedUpdateSamplesBar);
    document.documentElement.addEventListener('cart:change', debouncedUpdateSamplesBar);
    document.addEventListener('samples:added', debouncedUpdateSamplesBar);
    document.addEventListener('samples:removed', debouncedUpdateSamplesBar);
    
    document.addEventListener('line-item:removed', debouncedUpdateSamplesBar);
  }

  // Product Card Sample Handler
  initSampleCardHandler() {
    if (window.drawerSampleCardHandlerInitialized) return;
    window.drawerSampleCardHandlerInitialized = true;
    
    document.addEventListener('click', (event) => {
      const card = event.target.closest('.sample-product-card');
      if (!card) return;
      
      if (card.classList.contains('sample-product-card--adding')) return;
      
      const productId = card.dataset.productId;
      const productTitle = card.dataset.productTitle;
      
      if (!productId) return;
      
      // Skip cart page behavior (handled in main-cart.js)
      const isOnCartPage = document.querySelector('.shopify-section--main-cart') !== null;
      if (isOnCartPage) return;
      
      // Drawer behavior
      card.classList.add('sample-product-card--adding');
      
      // Remove all existing samples before adding the new one
      fetch('/cart.js')
      .then(response => response.json())
      .then(async cartData => {
        // Find all existing sample products in cart
        const removePromises = [];
        for (const item of cartData.items) {
          // Check if item is a sample (not a free gift)
          const isFreeGift = item.properties && item.properties._free_gift;
          if (!isFreeGift) {
            const isSample = await CartDrawerUtils.isSampleProduct(item.handle);
            if (isSample) {
              removePromises.push(
                fetch('/cart/change.js', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                  },
                  body: JSON.stringify({
                    id: item.key,
                    quantity: 0
                  })
                })
              );
            }
          }
        }
        return Promise.all(removePromises);
      })
      .then(() => {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: productId,
            quantity: 1
          })
        });
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to add sample to cart');
        }
        return response.json();
      })
      .then(data => {
        card.classList.remove('sample-product-card--adding');
        
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('samples:added', { bubbles: true }));
        
        document.dispatchEvent(new CustomEvent('toast:show', {
          detail: {
            message: `${productTitle} sample added to cart!`
          }
        }));
        
        setTimeout(() => {
          const samplesDrawer = document.getElementById('samples-drawer');
          if (samplesDrawer && samplesDrawer.open) {
            samplesDrawer.hide();
          }
        }, 200);
      })
      .catch(error => {
        card.classList.remove('sample-product-card--adding');
        
        document.dispatchEvent(new CustomEvent('toast:show', {
          detail: {
            message: 'Sorry, there was an error adding the sample to your cart.',
            tone: 'error'
          }
        }));
        
        console.error('Error adding sample to cart:', error);
      });
    });
  }
}

// Free Gift Manager for Cart Drawer
class DrawerFreeGiftManager {
  constructor(config) {
    this.gift1Config = config.gift1 || null;
    this.gift2Config = config.gift2 || null;
    this.isProcessing = false;
    this.init();
  }
  
  init() {
    document.addEventListener('cart:change', async (event) => {
      await this.checkAndManageFreeGifts(event.detail.cart);
    });
    
    document.addEventListener('DOMContentLoaded', () => {
      this.checkInitialCart();
    });
  }
  
  async checkInitialCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      await this.checkAndManageFreeGifts(cart);
    } catch (error) {
      console.error('Error checking initial cart:', error);
    }
  }
  
  async checkAndManageFreeGifts(cart) {
    if (this.isProcessing) return;
    
    // Track all free gifts to identify orphaned ones (not matching current config)
    let hasGift1 = false;
    let hasGift2 = false;
    let gift1Item = null;
    let gift2Item = null;
    const orphanedFreeGifts = [];
    
    cart.items.forEach(item => {
      const isFreeGift = item.properties && item.properties._free_gift;
      if (isFreeGift) {
        const matchesGift1 = this.gift1Config && item.handle === this.gift1Config.freeGiftHandle;
        const matchesGift2 = this.gift2Config && item.handle === this.gift2Config.freeGiftHandle;
        
        if (matchesGift1) {
          hasGift1 = true;
          gift1Item = item;
        } else if (matchesGift2) {
          hasGift2 = true;
          gift2Item = item;
        } else {
          // This is a free gift that doesn't match current configuration - mark for removal
          orphanedFreeGifts.push(item);
        }
      }
    });
    
    // Remove any free gifts that don't match the current configuration
    if (orphanedFreeGifts.length > 0) {
      // Remove the first orphaned gift - this will trigger a cart refresh
      // and the function will be called again to remove the next one
      this.removeFreeGift(cart, orphanedFreeGifts[0].handle);
      return; // Exit early, will be called again after cart updates
    }

    // Remove duplicate GWP products added without _free_gift property
    const giftHandles = [
      this.gift1Config?.freeGiftHandle,
      this.gift2Config?.freeGiftHandle
    ].filter(Boolean);

    const duplicateGifts = cart.items.filter(item => {
      const isFreeGift = item.properties && item.properties._free_gift;
      return !isFreeGift && giftHandles.includes(item.handle);
    });

    if (duplicateGifts.length > 0) {
      await CartDrawerUtils.removeCartItems(duplicateGifts);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return; // Exit early, will be called again after cart updates
    }

    // Fix free gift quantities if they're not 1
    if (hasGift1 && gift1Item && gift1Item.quantity !== 1) {
      this.fixFreeGiftQuantity(gift1Item.key);
      return; // Exit early, will be called again after cart updates
    }
    if (hasGift2 && gift2Item && gift2Item.quantity !== 1) {
      this.fixFreeGiftQuantity(gift2Item.key);
      return; // Exit early, will be called again after cart updates
    }
    
    // Check if we have eligible products (excluding excluded products if no eligible products exist)
    const { total: eligibleTotal, hasEligible } = await CartDrawerUtils.getEligibleCartTotal(cart.items);
    
    // If there are no eligible products, remove all free gifts
    if (!hasEligible) {
      if (hasGift1) {
        this.removeFreeGift(cart, this.gift1Config.freeGiftHandle);
      }
      if (hasGift2) {
        this.removeFreeGift(cart, this.gift2Config.freeGiftHandle);
      }
      return;
    }
    
    // Calculate cart total using eligible total (which includes excluded products if eligible products exist)
    let cartTotal = eligibleTotal;
    
    // Subtract cart-level discounts
    if (cart.cart_level_discount_applications) {
      cart.cart_level_discount_applications.forEach(discount => {
        cartTotal -= discount.total_allocated_amount;
      });
    }
    
    // Determine which gift should be present
    const threshold1 = this.gift1Config ? this.gift1Config.threshold : Infinity;
    const threshold2 = this.gift2Config ? this.gift2Config.threshold : Infinity;
    
    let shouldHaveGift1 = false;
    let shouldHaveGift2 = false;
    
    if (threshold2 <= threshold1) {
      // Threshold 2 is lower or equal - prioritize gift 2
      if (cartTotal >= threshold2 && this.gift2Config) {
        shouldHaveGift2 = true;
      } else if (cartTotal >= threshold1 && this.gift1Config) {
        shouldHaveGift1 = true;
      }
    } else {
      // Threshold 1 is lower - normal priority
      if (cartTotal >= threshold2 && this.gift2Config) {
        shouldHaveGift2 = true;
      } else if (cartTotal >= threshold1 && this.gift1Config) {
        shouldHaveGift1 = true;
      }
    }
    
    // Manage gifts based on what should be present
    if (shouldHaveGift2) {
      // Should have gift 2 - remove gift 1 if present, add gift 2 if not present
      if (hasGift1 && !hasGift2) {
        // Remove gift 1 first, then add gift 2
        this.removeFreeGift(cart, this.gift1Config.freeGiftHandle, async () => {
          await this.addFreeGift(this.gift2Config, 'Free gift 2 added to your cart!');
        });
      } else if (hasGift1 && hasGift2) {
        // Both present but should only have gift 2 - remove gift 1
        this.removeFreeGift(cart, this.gift1Config.freeGiftHandle);
      } else if (!hasGift1 && !hasGift2) {
        // Neither present - add gift 2
        this.addFreeGift(this.gift2Config, 'Free gift 2 added to your cart!');
      }
    } else if (shouldHaveGift1) {
      // Should have gift 1 - remove gift 2 if present, add gift 1 if not present
      if (hasGift2 && !hasGift1) {
        // Remove gift 2 first, then add gift 1
        this.removeFreeGift(cart, this.gift2Config.freeGiftHandle, async () => {
          await this.addFreeGift(this.gift1Config, 'Free gift added to your cart!');
        });
      } else if (hasGift1 && hasGift2) {
        // Both present but should only have gift 1 - remove gift 2
        this.removeFreeGift(cart, this.gift2Config.freeGiftHandle);
      } else if (!hasGift1 && !hasGift2) {
        // Neither present - add gift 1
        this.addFreeGift(this.gift1Config, 'Free gift added to your cart!');
      }
    } else {
      // Should have no gifts - remove both if present
      if (hasGift1) {
        this.removeFreeGift(cart, this.gift1Config.freeGiftHandle);
      }
      if (hasGift2) {
        this.removeFreeGift(cart, this.gift2Config.freeGiftHandle);
      }
    }
  }
  
  async addFreeGift(giftConfig, message) {
    if (this.isProcessing || !giftConfig) return;
    this.isProcessing = true;
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: giftConfig.freeGiftProductId,
          quantity: 1,
          properties: { '_free_gift': 'true' }
        })
      });
      
      if (response.ok) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        
        if (message) {
          document.dispatchEvent(new CustomEvent('toast:show', {
            detail: { message }
          }));
        }
      }
    } catch (error) {
      console.error('Error adding free gift:', error);
    } finally {
      this.isProcessing = false;
    }
  }
  
  async removeFreeGift(cart, giftHandle, callback) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    
    try {
      const freeGiftItem = cart.items.find(item => item.handle === giftHandle && item.properties && item.properties._free_gift);

      if (freeGiftItem) {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: freeGiftItem.key,
            quantity: 0
          })
        });
        
        if (response.ok) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          if (callback) {
            // Wait a bit for cart to update before callback
            setTimeout(callback, 300);
          }
        }
      }
    } catch (error) {
      console.error('Error removing free gift:', error);
    } finally {
      this.isProcessing = false;
    }
  }
  
  async fixFreeGiftQuantity(lineKey) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 1
        })
      });
      
      if (response.ok) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      console.error('Error fixing free gift quantity:', error);
    } finally {
      this.isProcessing = false;
    }
  }
}

// Sample Cleanup Manager for Cart Drawer
class DrawerSampleCleanupManager {
  constructor() {
    this.isProcessing = false;
    this.init();
  }
  
  init() {
    document.addEventListener('cart:change', (event) => {
      this.checkAndCleanupSamples(event.detail.cart);
    });
    
    document.addEventListener('cart:refresh', () => this.checkInitialCart());
    document.documentElement.addEventListener('cart:refresh', () => this.checkInitialCart());
    
    document.addEventListener('DOMContentLoaded', () => {
      this.checkInitialCart();
    });
  }
  
  async checkInitialCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.checkAndCleanupSamples(cart);
    } catch (error) {
      console.error('Error checking initial cart for samples:', error);
    }
  }
  
  async checkAndCleanupSamples(cart) {
    if (this.isProcessing) return;
    
    const { regularProducts, sampleProducts } = await CartDrawerUtils.categorizeCartItems(cart.items);
    
    // Fix sample quantities if they're not 1
    for (const sample of sampleProducts) {
      if (sample.quantity !== 1) {
        this.fixSampleQuantity(sample.key);
        return; // Exit early, will be called again after cart updates
      }
    }
    
    // If no regular products but there are samples, remove all samples
    if (regularProducts.length === 0 && sampleProducts.length > 0) {
      this.removeAllSamples(sampleProducts);
      return;
    }
    
    // Ensure only 1 sample exists - keep the most recent one (last in array), remove others
    if (sampleProducts.length > 1) {
      const samplesToRemove = sampleProducts.slice(0, -1); // Keep the last one, remove the rest
      this.removeSamples(samplesToRemove);
    }
  }
  
  async removeSamples(sampleProducts) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    
    try {
      await CartDrawerUtils.removeCartItems(sampleProducts);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (error) {
      console.error('Error removing samples:', error);
    } finally {
      this.isProcessing = false;
    }
  }
  
  async removeAllSamples(sampleProducts) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    
    try {
      await CartDrawerUtils.removeCartItems(sampleProducts);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (error) {
      console.error('Error removing samples:', error);
    } finally {
      this.isProcessing = false;
    }
  }
  
  async fixSampleQuantity(lineKey) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 1
        })
      });
      
      if (response.ok) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      console.error('Error fixing sample quantity:', error);
    } finally {
      this.isProcessing = false;
    }
  }
}

// Cart Offer Banner Manager
class CartOfferBannerManager {
  constructor(config) {
    this.conditionalEnabled = config?.conditionalEnabled || false;
    this.triggerCodes = config?.triggerCodes || [];
    this.bannerElement = null;
    this.init();
  }
  
  init() {
  }
  
  async checkInitialCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.updateBannerVisibility(cart);
    } catch (error) {
      console.error('Error checking cart for offer banner:', error);
    }
  }
  
  updateBannerVisibility(cart) {
    const bannerElement = document.getElementById('cart-offer-banner');
    if (!bannerElement) return;

    let shouldShow = false;
    
    if (!this.conditionalEnabled) {
      shouldShow = true;
    } else {
      if (this.triggerCodes.length === 0) {
        shouldShow = false;
      } else {
        if (cart.discount_applications) {
          for (const discount of cart.discount_applications) {
            if (discount.type === 'discount_code') {
              const appliedCode = discount.title.toLowerCase();
              if (this.triggerCodes.includes(appliedCode)) {
                shouldShow = true;
                break;
              }
            }
          }
        }
      }
    }
    
    if (shouldShow) {
      bannerElement.style.display = 'block';
    } else {
      bannerElement.style.display = 'none';
    }
  }
}

// Initialize the cart drawer manager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!window.cartDrawerManager) {
      window.cartDrawerManager = new CartDrawerManager();
    }
  });
} else {
  if (!window.cartDrawerManager) {
    window.cartDrawerManager = new CartDrawerManager();
  }
}

} // end __cartDrawerJsLoaded guard
